// The reader's dashboard notifications: a bell on the rail whose panel shows
// the most recent few, and the whole history as a page under User settings.
// Both read the same paginated inbox (lib/userNotificationClient.js), polled
// while the page is visible. Opening the panel or the page reads nothing as
// read; opening one notification marks only that one read.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { BellIcon, ExclamationTriangleIcon, KeyIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { listUserNotifications, markUserNotificationsRead, NOTIFICATION_TYPES, notificationType, updateUserNotification } from "../lib/userNotificationClient.js";
import { getFindingInvestigation } from "../lib/alertTriageClient.js";
import { ROUTER_PARAM } from "../lib/routeModel.js";
import { SECTION_BUTTON } from "./settings/SectionActions.jsx";
import Select from "./Select.jsx";
import { SkeletonRows } from "./ui/Skeleton.jsx";
import Tabs from "./ui/Tabs.jsx";

// How many of the newest the bell's panel shows; the page shows a server page.
const RECENT = 8;
const POLL_MS = 15000;
const MAX_BACKOFF_MS = 120000;
const LINK = "cursor-pointer border-0 bg-transparent p-0 text-[12.5px] text-[color:var(--green)] hover:underline disabled:cursor-default disabled:opacity-50";
// A placeholder row the height of a NotificationRow, so the list holds its size.
const ROW_SKELETON = "grid h-[72px] grid-cols-[26px_minmax(0,1fr)_28px] items-center gap-[10px] px-[14px]";
const QUIET = "cursor-pointer border-0 bg-transparent p-0 text-[11.5px] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline disabled:cursor-default disabled:opacity-50";

// One inbox query, polled every POLL_MS while the page is visible and again on
// focus, backing off after failures. `mutate` runs a change and refreshes,
// discarding any read that was in flight so its stale read state cannot win.
// `pending` is a load the reader started (the first, or a new filter or page)
// and not a background poll; until it answers, the last rows stay on screen
// marked `stale` so the list does not collapse and regrow.
const queryKey = (...parts) => JSON.stringify(parts);
function useNotificationFeed({ unreadOnly = false, kind = "", pageToken = "", pageSize = 50 } = {}) {
    const [data, setData] = useState(null);
    const [dataKey, setDataKey] = useState(null);
    const [settledKey, setSettledKey] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [tick, setTick] = useState(0);
    const generation = useRef(0);
    const refresh = useCallback(() => setTick((v) => v + 1), []);
    useEffect(() => {
        let alive = true, timer, inFlight = false, failures = 0;
        const abort = new AbortController();
        const current = ++generation.current;
        const key = queryKey(pageToken, unreadOnly, kind, pageSize);
        async function poll() {
            if (inFlight || document.visibilityState === "hidden") return;
            inFlight = true;
            try {
                const result = await listUserNotifications({ pageToken, unreadOnly, kind, pageSize, signal: abort.signal });
                if (alive && generation.current === current) { setData(result); setDataKey(key); setSettledKey(key); setError(""); }
                failures = 0;
            } catch {
                failures++;
                if (alive && generation.current === current) { setSettledKey(key); setError("Notifications could not be refreshed. Try again."); }
            } finally {
                inFlight = false;
                if (alive) { clearTimeout(timer); timer = setTimeout(poll, Math.min(MAX_BACKOFF_MS, POLL_MS * 2 ** failures)); }
            }
        }
        const wake = () => { if (document.visibilityState !== "hidden") { clearTimeout(timer); void poll(); } };
        void poll();
        window.addEventListener("focus", wake); document.addEventListener("visibilitychange", wake);
        return () => { alive = false; abort.abort(); clearTimeout(timer); window.removeEventListener("focus", wake); document.removeEventListener("visibilitychange", wake); };
    }, [pageToken, unreadOnly, kind, pageSize, tick]);
    const mutate = useCallback(async (work) => {
        setBusy(true); setError("");
        generation.current++;
        try { await work(); }
        catch { setError("Your action could not be completed. Try again."); }
        finally { setBusy(false); refresh(); }
    }, [refresh]);
    const key = queryKey(pageToken, unreadOnly, kind, pageSize);
    const current = data != null && dataKey === key;
    const pending = settledKey !== key;
    // A failed new query shows no rows: the last query's would answer the wrong filter.
    const items = current || pending ? data?.notifications || [] : [];
    return { data, items, current, pending, stale: !current && pending && data != null, busy, error, refresh, mutate };
}

// Where a notification leads: the finding, the assigned work, or the access request.
async function targetOf(item) {
    const next = new URLSearchParams();
    if (item.kind === "NOTIFICATION_KIND_NEW_FINDING") {
        next.set("mode", "workspace"); next.set("tab", `trace:${item.traceId}`); next.set("finding", item.objectId);
    } else if (["NOTIFICATION_KIND_FINDING_ASSIGNED", "NOTIFICATION_KIND_INVESTIGATION_ASSIGNED"].includes(item.kind)) {
        const investigation = await getFindingInvestigation(item.objectId);
        const finding = investigation.findings?.[0]?.finding_id;
        if (!finding) throw new Error("Assigned work is unavailable");
        next.set("mode", "triage"); next.set(ROUTER_PARAM.TRIAGE_FINDING, finding);
    } else {
        next.set("mode", "settings"); next.set("setting", "access/requests"); next.set(ROUTER_PARAM.NOTIFICATION_REQUEST, item.objectId);
    }
    return next;
}

function useFollow(feed, onDone) {
    const [, setParams] = useSearchParams();
    return (item) => feed.mutate(async () => {
        await updateUserNotification(item.id, { read: true });
        setParams(await targetOf(item));
        onDone?.();
    });
}

// Each group's mark: findings red, assignments green, temporary access blue.
const GROUP_ICON = {
    Findings: [ExclamationTriangleIcon, "var(--severity-critical)"],
    Assignments: [UserIcon, "var(--green)"],
    "Temporary access": [KeyIcon, "var(--graph)"],
};
function TypeIcon({ kind }) {
    const [Icon, color] = GROUP_ICON[notificationType(kind)?.group] || [BellIcon, "var(--muted)"];
    return (
        <span aria-hidden="true" className="flex h-[26px] w-[26px] items-center justify-center rounded-full" style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}>
            <Icon className="h-[14px] w-[14px]" />
        </span>
    );
}

const AT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
function ago(iso, now = Date.now()) {
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return "";
    const mins = Math.max(0, Math.round((now - ms) / 60000));
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    if (mins < 24 * 60) return `${Math.round(mins / 60)}h`;
    return AT.format(ms);
}

function NotificationRow({ item, busy, compact, onOpen, onToggleRead, onDismiss }) {
    const type = notificationType(item.kind);
    return (
        <li className="group grid grid-cols-[26px_minmax(0,1fr)_auto] items-start gap-[10px] border-t border-[color:var(--line)] px-[14px] py-[10px]" data-notification={item.id}>
            <TypeIcon kind={item.kind} />
            <div className="min-w-0">
                <button type="button" disabled={busy} className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left text-[color:var(--ink)] disabled:cursor-default" onClick={() => onOpen(item)}>
                    <span className={`block text-[13px] leading-[1.35] ${item.read ? "font-normal" : "font-semibold"}`}>{item.title}</span>
                    <span className="block text-[11.5px] text-[color:var(--muted)]">{type?.label}{compact ? "" : ` · ${AT.format(Date.parse(item.occurredAt))}`}</span>
                </button>
                {/* In the panel the row's actions show on hover or focus; on the page they always show. */}
                <div className={`mt-[4px] flex gap-[12px] ${compact ? "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100" : ""}`}>
                    <button type="button" disabled={busy} className={QUIET} onClick={() => onToggleRead(item)}>{item.read ? "Mark unread" : "Mark read"}</button>
                    <button type="button" disabled={busy} className={QUIET} onClick={() => onDismiss(item)}>Dismiss</button>
                </div>
            </div>
            <span className="flex items-center gap-[6px] whitespace-nowrap pt-[2px] text-[11.5px] text-[color:var(--muted)]">
                <time dateTime={item.occurredAt}>{ago(item.occurredAt)}</time>
                {item.read ? null : <span className="h-[8px] w-[8px] rounded-full bg-[color:var(--graph)]"><span className="sr-only">Unread</span></span>}
            </span>
        </li>
    );
}

// What the list says while it has nothing to show: still loading, failed, or
// verified empty, kept apart so a failed refresh never reads as an empty inbox.
// A failed refresh over rows already shown floats over them instead of pushing
// them down, and goes again without moving them when a later poll succeeds.
function FeedState({ feed, unreadOnly, rows }) {
    const { data, current, pending, stale, error, refresh, items } = feed;
    const failed = error ? <>{error} <button type="button" onClick={refresh} className={LINK}>Retry</button></> : null;
    return (
        <>
            {failed && items?.length ? (
                <div className="relative z-[2] h-0">
                    <p role="alert" className="absolute inset-x-[10px] top-[4px] m-0 rounded-[3px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] px-[10px] py-[6px] text-[12.5px] text-[color:var(--severity-critical)] shadow-md">{failed}</p>
                </div>
            ) : failed ? (
                <p role="alert" className="m-0 px-[14px] py-[16px] text-[13px] text-[color:var(--severity-critical)]">{failed}</p>
            ) : null}
            {!data && pending && !error ? <SkeletonRows rows={rows} cells={["w-[26px]", "w-[70%]", "w-[28px]"]} rowClassName={ROW_SKELETON} label="Loading notifications…" /> : null}
            {(current || stale) && !items.length && !error ? <p className="m-0 px-[14px] py-[16px] text-[13px] text-[color:var(--muted)]">{unreadOnly ? "No unread notifications." : "No notifications to show."}</p> : null}
        </>
    );
}

// The type filter's choices: every type, then each group's name over its types.
function typeOptions(groups) {
    return [
        { value: "", label: "All types" },
        ...groups.flatMap((g) => [
            { value: `group:${g}`, label: g, disabled: true },
            ...NOTIFICATION_TYPES.filter((t) => t.group === g).map((t) => ({ value: t.kind, label: <span className="pl-[10px]">{t.label}</span> })),
        ]),
    ];
}

function ShowToggle({ unreadOnly, unread, onChange }) {
    return (
        <Tabs
            mode="toggle"
            label="Show"
            items={[{ id: "all", label: "All" }, { id: "unread", label: unread > 0 ? `Unread ${unread}` : "Unread" }]}
            value={unreadOnly ? "unread" : "all"}
            onChange={(id) => onChange(id === "unread")}
        />
    );
}

// The bell on the rail, with the unread count, and its panel of the newest
// notifications. The rail clips what overflows it, so the panel is drawn on
// the page beside the rail.
export default function UserNotificationBell({ onOpenAll, onOpenPreferences }) {
    const [open, setOpen] = useState(false);
    const [unreadOnly, setUnreadOnly] = useState(false);
    const feed = useNotificationFeed({ unreadOnly, pageSize: open ? RECENT : 1 });
    const panel = useRef(null);
    const button = useRef(null);
    const close = useCallback((refocus = true) => { setOpen(false); if (refocus) button.current?.focus(); }, []);
    const follow = useFollow(feed, () => close(false));
    useEffect(() => {
        if (!open) return undefined;
        panel.current?.querySelector("button")?.focus();
        const onEvent = (event) => {
            if (event.type === "keydown" && event.key === "Escape") close();
            if (event.type === "pointerdown" && !panel.current?.contains(event.target) && !button.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("keydown", onEvent); document.addEventListener("pointerdown", onEvent);
        return () => { document.removeEventListener("keydown", onEvent); document.removeEventListener("pointerdown", onEvent); };
    }, [open, close]);
    const { data, busy, pending, stale, error } = feed;
    const count = data?.unreadCount;
    const label = `Notifications${count == null ? "" : `, ${count} unread`}${error ? ", refresh failed" : ""}`;
    return (
        <>
            <button
                ref={button}
                type="button"
                data-origin-mode="notifications"
                data-active={open ? "true" : "false"}
                className="relative"
                aria-label={label}
                title="Notifications"
                aria-expanded={open}
                aria-controls={open ? "personal-inbox" : undefined}
                onClick={() => setOpen((v) => !v)}
            >
                <BellIcon aria-hidden="true" />
                <span data-origin-mode-label="" aria-hidden="true">Notifications</span>
                {/* Folded, on the icon's corners; open, at the row's end on
                    its centerline, the count in a box (styles.css). */}
                {count > 0 || error ? (
                    <span data-nav-badges="" aria-hidden="true">
                        {error ? <span data-nav-alert="" /> : null}
                        {count > 0 ? <span data-nav-count="">{count > 99 ? "99+" : count}</span> : null}
                    </span>
                ) : null}
            </button>
            {open ? createPortal(
                <section
                    id="personal-inbox"
                    ref={panel}
                    aria-label="Notifications"
                    aria-busy={pending || busy}
                    className="fixed bottom-[52px] left-[calc(var(--origin-rail)+8px)] z-50 flex max-h-[min(600px,calc(100vh-80px))] w-[min(400px,calc(100vw-var(--origin-rail)-16px))] flex-col overflow-hidden rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] text-[color:var(--ink)] shadow-[0_16px_40px_rgba(0,0,0,0.18)]"
                >
                    <header className="flex items-center gap-[10px] px-[14px] pb-[8px] pt-[12px]">
                        <h2 className="m-0 flex-1 text-[15px] font-semibold">Notifications</h2>
                        <button type="button" className={LINK} disabled={busy || !data?.readBoundary || !count} onClick={() => feed.mutate(() => markUserNotificationsRead(data.readBoundary))}>Mark all read</button>
                        <button type="button" aria-label="Close notifications" className="cursor-pointer border-0 bg-transparent p-[2px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" onClick={() => close()}><XMarkIcon className="h-[16px] w-[16px]" /></button>
                    </header>
                    <div className="px-[10px] pb-[8px]"><ShowToggle unreadOnly={unreadOnly} unread={count} onChange={setUnreadOnly} /></div>
                    {/* One height whichever filter shows, so switching between All and
                        Unread never resizes the panel; the list scrolls inside it. */}
                    <div className="flex h-[min(360px,calc(100vh-240px))] min-h-0 flex-col" data-inbox-list="">
                        <FeedState feed={feed} unreadOnly={unreadOnly} rows={3} />
                        <ul className={`m-0 min-h-0 flex-1 list-none overflow-y-auto p-0 transition-opacity ${stale ? "opacity-50" : ""}`}>
                            {feed.items.slice(0, RECENT).map((item) => (
                                <NotificationRow key={item.id} item={item} busy={busy} compact onOpen={follow}
                                    onToggleRead={(n) => feed.mutate(() => updateUserNotification(n.id, { read: !n.read }))}
                                    onDismiss={(n) => feed.mutate(() => updateUserNotification(n.id, { dismiss: true }))} />
                            ))}
                        </ul>
                    </div>
                    <footer className="flex items-center justify-between border-t border-[color:var(--line)] px-[14px] py-[10px]">
                        <button type="button" className={`${LINK} font-medium`} onClick={() => { close(false); onOpenAll(); }}>See all notifications</button>
                        {onOpenPreferences ? <button type="button" className={QUIET} onClick={() => { close(false); onOpenPreferences(); }}>Preferences</button> : null}
                    </footer>
                </section>,
                document.body,
            ) : null}
        </>
    );
}

// Every notification, newest first, a server page at a time, filtered by
// read state and type.
export function UserNotificationHistory() {
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [kind, setKind] = useState("");
    const [pageToken, setPageToken] = useState("");
    const feed = useNotificationFeed({ unreadOnly, kind, pageToken });
    const follow = useFollow(feed);
    const { data, busy, pending, stale } = feed;
    const count = data?.unreadCount;
    const restart = (apply) => { setPageToken(""); apply(); };
    const groups = [...new Set(NOTIFICATION_TYPES.map((t) => t.group))];
    return (
        <section aria-label="Notification history" aria-busy={pending || busy} className="grid min-w-0 gap-[12px]" data-notification-history="">
            <div className="flex flex-wrap items-center gap-[12px]">
                <ShowToggle unreadOnly={unreadOnly} unread={count} onChange={(v) => restart(() => setUnreadOnly(v))} />
                <div className="w-[220px]">
                    <Select
                        value={kind}
                        onChange={(next) => restart(() => setKind(next))}
                        options={typeOptions(groups)}
                        ariaLabel="Notification type"
                    />
                </div>
                <span className="flex-1" />
                <button type="button" className={SECTION_BUTTON} disabled={busy || !data?.readBoundary || !count} onClick={() => feed.mutate(() => markUserNotificationsRead(data.readBoundary))}>Mark all read</button>
            </div>
            <div className="border-b border-[color:var(--line)]">
                <FeedState feed={feed} unreadOnly={unreadOnly} rows={6} />
                <ul className={`m-0 list-none p-0 transition-opacity ${stale ? "opacity-50" : ""}`}>
                    {feed.items.map((item) => (
                        <NotificationRow key={item.id} item={item} busy={busy} onOpen={follow}
                            onToggleRead={(n) => feed.mutate(() => updateUserNotification(n.id, { read: !n.read }))}
                            onDismiss={(n) => feed.mutate(() => updateUserNotification(n.id, { dismiss: true }))} />
                    ))}
                </ul>
            </div>
            <div className="flex gap-[8px]">
                {pageToken ? <button type="button" className={SECTION_BUTTON} disabled={busy || pending} onClick={() => setPageToken("")}>Back to newest</button> : null}
                {data?.nextPageToken ? <button type="button" className={SECTION_BUTTON} disabled={busy || pending} onClick={() => setPageToken(data.nextPageToken)}>Older notifications</button> : null}
            </div>
        </section>
    );
}
